import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { useAuthSession } from '../hooks/useAuthSession';

export type Pillar = 'dna' | 'backtesting' | 'portfolio' | 'risk' | 'wallet' | 'market' | 'asset';

interface PillarNavProps {
  activePillar: Pillar;
  onPillarChange: (pillar: Pillar) => void;
}

const PILLARS: { key: Pillar; labelKey: string }[] = [
  { key: 'dna', labelKey: 'pillar.dna' },
  { key: 'backtesting', labelKey: 'pillar.backtesting' },
  { key: 'portfolio', labelKey: 'pillar.portfolio' },
  { key: 'risk', labelKey: 'pillar.risk' },
  { key: 'wallet', labelKey: 'pillar.wallet' },
  { key: 'market', labelKey: 'pillar.market' },
  { key: 'asset', labelKey: 'pillar.asset' },
];

type ThemeMode = 'dark' | 'light' | 'high-contrast';

const THEME_STORAGE_KEY = 'tm-theme-mode';
const THEME_SEQUENCE: ThemeMode[] = ['dark', 'light', 'high-contrast'];

function loadThemeMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'high-contrast' ? stored : 'dark';
  } catch {
    return 'dark';
  }
}

function applyThemeMode(mode: ThemeMode) {
  const root = document.documentElement;
  if (mode === 'dark') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', mode);
  }
}

const WalletConnectButton = lazy(() =>
  import('../features/wallet/components/WalletConnectButton').then((module) => ({ default: module.WalletConnectButton })),
);

function WalletConnectButtonFallback() {
  const { t } = useTranslation();
  return (
    <span data-testid="wallet-chip" className="text-[10px] font-mono text-muted-foreground/60">
      {t('wallet.external_na')}
    </span>
  );
}

export function PillarNav({ activePillar, onPillarChange }: PillarNavProps) {
  const { locale, setLocale, t } = useTranslation();
  const [themeMode, setThemeMode] = useState<ThemeMode>(loadThemeMode);
  const { email, loading, error } = useAuthSession();

  useEffect(() => {
    applyThemeMode(themeMode);
  }, [themeMode]);

  const toggleLocale = useCallback(() => {
    setLocale(locale === 'en' ? 'ko' : 'en');
  }, [locale, setLocale]);

  const cycleTheme = useCallback(() => {
    setThemeMode((prev) => {
      const next = THEME_SEQUENCE[(THEME_SEQUENCE.indexOf(prev) + 1) % THEME_SEQUENCE.length] ?? 'dark';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // localStorage unavailable
      }
      return next;
    });
  }, []);

  return (
    <header
      className="flex flex-wrap items-center min-h-12 px-3 py-2 lg:h-12 lg:flex-nowrap lg:px-4 lg:py-0 border-b border-border shrink-0 bg-card gap-x-3 gap-y-2 lg:gap-4"
      data-testid="pillar-nav"
    >
      {/* Logo */}
      <div className="order-1 flex items-center gap-2 shrink-0">
        <div className="w-5 h-5 bg-primary rounded-sm flex items-center justify-center">
          <div className="w-2 h-2 bg-background rounded-sm" />
        </div>
        <span className="font-mono text-sm font-bold text-primary tracking-wider">TRADE_MATRIX</span>
      </div>

      {/* Separator */}
      <div className="hidden lg:block w-px h-6 bg-border shrink-0" />

      {/* Pillar nav */}
      <nav
        className="order-3 basis-full flex-none flex items-center gap-0.5 min-w-0 overflow-x-auto lg:order-none lg:basis-auto lg:flex-1"
        aria-label={t('a11y.pillar_navigation')}
      >
        {PILLARS.map(({ key, labelKey }) => {
          const isActive = activePillar === key;
          const label = t(labelKey);
          return (
            <button
              key={key}
              onClick={() => onPillarChange(key)}
              aria-current={isActive ? 'page' : undefined}
              data-testid={`pillar-nav-${key}`}
              className={[
                'relative shrink-0 px-3 py-1 text-[10px] font-mono uppercase transition-colors',
                'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px]',
                isActive
                  ? 'text-foreground font-bold after:bg-primary'
                  : 'text-muted-foreground hover:text-foreground after:bg-transparent',
              ].join(' ')}
            >
              {label}
              {isActive && (
                <span className="ml-1.5 text-[8px] font-mono text-primary/80 bg-primary/10 px-1 py-0.5 rounded-sm border border-primary/30">
                  {t('pillar.active_badge')}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Market ticker */}
      <div className="hidden 2xl:flex items-center gap-2 shrink-0">
        <div className="font-mono text-[9px] border border-border px-3 py-1 bg-card rounded flex items-center gap-1.5">
          <span className="text-muted-foreground">VIX_CRYPTO</span>
          <span className="text-red-400 font-bold animate-pulse">72.4↑</span>
        </div>
        <div className="font-mono text-[9px] border border-border px-3 py-1 bg-card rounded flex items-center gap-1.5">
          <span className="text-muted-foreground">GLOBAL_LIQ</span>
          <span className="text-success">$14.2T</span>
        </div>
        <div className="font-mono text-[9px] border border-border px-3 py-1 bg-card rounded flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
          <span className="text-success">{t('market.synced')}</span>
        </div>
      </div>

      {/* Separator */}
      <div className="hidden lg:block w-px h-6 bg-border shrink-0" />

      {/* Controls */}
      <div className="order-2 ml-auto flex items-center gap-1.5 shrink-0 lg:order-none lg:ml-0">
        <Suspense fallback={<WalletConnectButtonFallback />}>
          <WalletConnectButton />
        </Suspense>
        <button
          onClick={toggleLocale}
          className="px-1.5 py-0.5 text-[10px] font-mono border border-border rounded-sm hover:bg-card-elevated transition-colors text-muted-foreground hover:text-foreground"
          aria-label={t('lang.aria')}
        >
          {locale === 'en' ? 'KO' : 'EN'}
        </button>
        <button
          onClick={cycleTheme}
          className={`px-1.5 py-0.5 min-w-10 text-[10px] font-mono border rounded-sm transition-colors ${
            themeMode !== 'dark'
              ? 'border-primary text-primary bg-primary/10'
              : 'border-border text-muted-foreground hover:bg-card-elevated hover:text-foreground'
          }`}
          aria-label={t('theme.aria')}
          title={t(`theme.${themeMode}.title`)}
        >
          {t(`theme.${themeMode}.short`)}
        </button>

        {/* User session chip */}
        {loading ? (
          <span
            aria-label={t('auth.loading_user_session')}
            className="px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground/50 select-none"
          >
            ...
          </span>
        ) : error ? (
          <a
            href="/cdn-cgi/access/logout"
            aria-label={t('auth.error_sign_out')}
            title={error.message}
            className="px-1.5 py-0.5 text-[10px] font-mono border border-red-800/50 rounded-sm text-red-400/70 hover:text-red-400 hover:border-red-800 transition-colors"
          >
            ⚠
          </a>
        ) : email ? (
          <span
            aria-label={`Signed in as ${email}`}
            data-testid="user-chip"
            className="px-1.5 py-0.5 text-[10px] font-mono border border-border rounded-sm text-muted-foreground bg-card truncate max-w-[140px]"
          >
            {email}
          </span>
        ) : null}
      </div>
    </header>
  );
}
